import type { PortalUpcomingBooking } from '../../types/portalLeadBot';

// Pure display helpers for the portal's "Lead conversations" page (no React, no I/O).

export type UpcomingDay = { dayKey: string; dayLabel: string; items: Array<PortalUpcomingBooking & { timeLabel: string }> };

function fmt(options: Intl.DateTimeFormatOptions, tz: string): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat('en-AU', { ...options, timeZone: tz });
}

/** "Thu 15 Oct" for an instant, in the club's zone. */
export function dayLabelFor(iso: string, tz: string): string {
  // Built from parts, not format(): the joined string differs between ICU builds ("Sat, 17 Oct").
  const parts = Object.fromEntries(
    fmt({ weekday: 'short', day: 'numeric', month: 'short' }, tz)
      .formatToParts(new Date(iso))
      .map((p) => [p.type, p.value]),
  );
  return `${parts.weekday} ${parts.day} ${parts.month}`;
}

/** "10:30 am" for an instant, in the booking's own zone. */
export function timeLabelFor(iso: string, tz: string): string {
  return fmt({ hour: 'numeric', minute: '2-digit', hour12: true }, tz).format(new Date(iso)).replace(/\s/g, ' ').toLowerCase();
}

/** Upcoming bookings grouped by the day they fall on in their own zone, soonest first. */
export function groupUpcomingByDay(
  bookings: readonly PortalUpcomingBooking[],
  fallbackTz: string,
): UpcomingDay[] {
  const sorted = [...bookings].sort((a, b) => a.startsAt.localeCompare(b.startsAt));
  const days: UpcomingDay[] = [];
  for (const b of sorted) {
    const tz = b.timezone || fallbackTz;
    const dayLabel = dayLabelFor(b.startsAt, tz);
    const dayKey = fmt({ year: 'numeric', month: '2-digit', day: '2-digit' }, tz).format(new Date(b.startsAt));
    let day = days.find((d) => d.dayKey === dayKey);
    if (!day) {
      day = { dayKey, dayLabel, items: [] };
      days.push(day);
    }
    day.items.push({ ...b, timeLabel: timeLabelFor(b.startsAt, tz) });
  }
  return days;
}

export const WINDOW_LABELS = { today: 'Today', days7: 'Last 7 days', days30: 'Last 30 days' } as const;

export const LEAD_FILTER_LABELS = { attention: 'Needs attention', all: 'All', booked: 'Booked' } as const;
export type LeadFilterKey = keyof typeof LEAD_FILTER_LABELS;

/** The one sentence on the page for each reason a lead is flagged, in the club's terms. */
export const DISTRESS_BANNER =
  'Possible distress: please call this person. The assistant sent a kind message with a support line and has stopped. Nothing further is sent automatically.';
